/**
 * Search Service. Responsible for maintaining search state and preparing
 * search results for UI.
 */
import _ from 'lodash';
import MessageBusTopics from '../../../../common/MessageBusTopics';
import moment from 'moment-timezone';
import searchFunnelBackApiService from './SearchFunnelBackApiService';

var defaultConfig = {
    funnelBackUrl: '',
    eventResultsPerPage: 5,
    currentPage: 1,
};

/**
 * Default state configurator
 *
 * @param {Object} config configuration object
 *
 * @returns {Object} a configured default state object
 *
 * @private
 */
function defaultState(config) {
    return {
        searchString: '',
        recentlyRequestedSearchString: '',
        events: [],
        totalEvents: 0,
        newEvents: [],
        sports: [],
        totalSports: 0,
        customSearches: [],
        pagination: {
            currentPage: config.currentPage,
            totalPages: undefined,
            resultsPerPage: config.resultsPerPage,
        },
        startTime: null,
        endTime: null,
        error: false,
        errorData: '',
    };
}

/**
 * Search Service Constructor
 *
 * @param {Object} config  configuration object
 * @param {Object} messageBus  WH message bus
 * @param {Object} lang  WH language
 * @param {Object} i18n  WH internationalization
 *
 * @constructor
 */
function SearchService(config, deps) {

    this.deps = deps || {};

    this.config = _.extend({}, defaultConfig, config);
    this.lang = config.lang;
    this.i18n = config.i18n;
    var state = {};

    this.setState = function (newState) {
        state = _.extend({}, state, newState);
    };

    this.getState = function () {
        // Readonly state
        return _.extend({}, state);
    };

    this.resetState = function () {
        state = defaultState(this.config);
        return state;
    };

    this.abortRequest = function () {
        searchFunnelBackApiService.abortRequest();
    };

    this.resetState();
}

/**
 * Prepares the search for and calls Search API Service
 *
 * @param {String} searchTerm  The search key
 * @param {Number} page  The page number (paginated)
 *
 * @returns {Promise}
 *
 * @public
 */
SearchService.prototype.search = function (searchTerm, page) {
    // if not on the first page you need add 1 to the offset
    var addToOffset = (page === 1) ? 0 : 1;
    var limit = this.config.eventResultsPerPage;
    var offset = ((page - 1) * limit) + addToOffset;
    var culture = this.i18n.locale;
    // Remember original search string and startTime
    this.setState({
        searchString: searchTerm,
        startTime: moment().valueOf(),
    });

    return searchFunnelBackApiService.search(this.config.funnelBackUrl, searchTerm, limit, offset, culture)
        .done(this.successfulSearchHandler.bind(this))
        .fail(this.unsuccessfulSearchHandler.bind(this));
};

/**
 * The successful callback for the Search API Service
 *
 * @param {Object} data - response object
 *
 * @returns {void}
 *
 * @private
 */
SearchService.prototype.successfulSearchHandler = function (data) {

    var state = this.getState();
    var endTime = moment().valueOf();

    /*jshint -W106 */
    var resultsPerPage = data.pagination.ranks_per_page,
        totalEvents = data.pagination.total_ranks,
        totalPages = Math.ceil(totalEvents / resultsPerPage),
        currentPage = Math.ceil(data.pagination.current_end / resultsPerPage);
    /*jshint +W106 */

    this.sendStat(state.searchString, totalEvents, state.startTime, endTime);

    var results = data.results.map(this.renderableEventsResultsItemFilter.bind(this)),
        sportResults = data.sportResults.map(this.renderableSportsResultsItemFilter.bind(this)),
        customResults = [];

    if (data.hasOwnProperty('customResults')) {
        customResults = data.customResults
            .filter(this.filterDeviceSpecificCustomResultsItems.bind(this))
            .map(this.renderableCustomResultsItemFilter.bind(this));
    }

    this.setState({
        recentlyRequestedSearchString: state.searchString,
        events: state.events.concat(results),
        totalEvents: totalEvents,
        newEvents: results,
        sports: sportResults,
        totalSports: sportResults.length,
        custom: customResults,
        totalCustom: customResults.length,
        endTime: endTime,
        pagination: {
            resultsPerPage: resultsPerPage,
            totalPages: totalPages,
            currentPage: currentPage,
        },
    });
};

/**
 * Sending search result
 *
 * @param {String} text Text to search
 * @param {Number} total The number of results
 * @param {Number} startQueryTime The query start time
 * @param {Number} endQueryTime The query end time
 *
 * @returns {void}
 *
 * @private
 */
SearchService.prototype.sendStat = function (text, total, startQueryTime, endQueryTime) {

    window.WH.messageBus.publish(MessageBusTopics.SEARCH_RESULT, {
        query: text,
        total: total,
        time: endQueryTime - startQueryTime,
    });
};

/**
 * The fail callback for the Search API Searvice
 *
 * @param {Object} err  Error object
 *
 * @returns {void}
 *
 * @private
 */
SearchService.prototype.unsuccessfulSearchHandler = function (err) {

    this.setState({
        error: true,
        errorData: err,
    });
};

/**
 * Generate a url given a event search item
 *
 * @param  {Object} item Event search result item
 *
 * @return {String}      Valid url
 *
 * @private
 */
SearchService.prototype.generateEventUrl = function generateEventUrl(item) {

    var url = window.location.href.split(this.lang)[0] + this.lang + '/';

    url += this.deps.Translator.translateSlug(item.sportName
        .toLocaleLowerCase()
        .replace(/[.|:]/g, '')
        .replace(/[\s\\/]/g, '-'));
    url += '/' + item.id;
    url += '/' + item.name.toLocaleLowerCase().replace(/[.|:]/g, '')
        .replace(/[\s\\/]/g, '-');

    return url;
};

/**
 * Create a UI renderable events results item given a search result item
 *
 * @param  {Object} eventsItem Event search result item as given by SearchService
 *
 * @return {Object}      Renderable events result object
 *
 * @private
 */
SearchService.prototype.renderableEventsResultsItemFilter = function (eventsItem) {

    return {
        isInPlay: eventsItem.isInPlay,
        streamingAvailable: eventsItem.streamingAvailable,
        url: this.generateEventUrl(eventsItem),
        name: eventsItem.name.replace(/\|/g, ''),
        marketCountActiveTotal: eventsItem.marketCountActiveTotal,
        competitionName: eventsItem.parentName.replace(/\|/g, ''),
        marketTemplate: eventsItem.marketTemplate,
        startDate: this.formatDate(eventsItem.startDateTime.replace('.000+0000', '+00:00')),
    };
};

/**
 * Filter out custom result items not suitable for the current device
 *
 * @param  {Object} item Custom Result item
 *
 * @return {Boolean}
 */
SearchService.prototype.filterDeviceSpecificCustomResultsItems = function (item) {

    var props = item.additionalProperties,
        isNative = this.deps.DeviceDetector.isNative();

    return isNative ? props.showOnNative : props.showOnWeb;
};

/**
 * Create a UI renderable custom results item
 *
 * @param  {Object} customResult Custom Result item as given by SearchService
 *
 * @return {Object}      Renderable custom result object
 *
 * @private
 */
SearchService.prototype.renderableCustomResultsItemFilter = function (customResult) {

    var props = customResult.additionalProperties;

    return {
        url: customResult.displayUrl,
        title: props.iconText,
        iconUrl: props.iconUrl,
        openNewWin: props.openNewWin,
    };
};

/**
 * Format date/time string
 *
 * @param   {String} dateString Date time string to be formatted (eg. 2013-10-07T13:50:00.000+0000)
 *
 * @returns {String}            Formatted date/time string
 *
 * @private
 */
SearchService.prototype.formatDate = function (dateString) {

    var date = moment(dateString);

    if (date.isSame(moment.utc(), 'day')) {

        return this.deps.Date.format(date, 'HH:mm');
    }

    return this.deps.Date.format(date, 'DD MMM HH:mm');
};

/**
 * Create a UI renderable sports results item given a search result item
 *
 * @param  {Object} sportsItem Sports search result item as given by SearchService
 *
 * @return {Object}      Renderable sports result object
 *
 * @private
 */
SearchService.prototype.renderableSportsResultsItemFilter = function (sportsItem) {

    // Mappings for items that don't follow standard name/url pattern
    var sportNamesMap = {
        '|Virtual Sports|': {
            name: 'Virtual World',
            url: 'apps/virtual/horse-racing/flat',
            icon: 'virtual-world',
        },
        '|Yachting/Sailing|': {
            name: 'Yachting/Sailing',
            url: 'yachting-sailing',
            icon: 'sailing',
        },
    };

    const handleIconsForTranslatedNames = (sport) => {
        const sportName = sport.replace(/[\s\/]/g, '-').toLowerCase();
        const locale = window.WH.i18n.locale;

        if ( locale === 'es-es' || locale === 'it-it') {
            const translations = window.WH.i18n.slugDict.slugs;
            return translations[sportName] || sportName;
        }

        return sportName;
    };

    // Default values
    var sportName = sportsItem.name.replace(/\|/g, ''), // Remove delimiters
        sportUrl = sportName.replace(/[\s\/]/g, '-').toLowerCase(), // Default slug generated from label
        sportIcon = handleIconsForTranslatedNames(sportName);

    // If a mapping exists override default values.
    if (sportNamesMap.hasOwnProperty(sportsItem.name)) {

        sportName = sportNamesMap[sportsItem.name].name;
        sportUrl = sportNamesMap[sportsItem.name].url;
        sportIcon = sportNamesMap[sportsItem.name].icon;
    }

    return {
        title: sportName,
        url: this.deps.URLProvider.highlights.base({
            locale: this.i18n.locale,
            sport: sportUrl,
        }),
        icon: sportIcon,
    };
};

export default SearchService;
    